/* eslint-disable */
import toastStore from '@/store/toast.store';
import i18n from '@/i18n';
import { appEvents } from '@/store/app.events';
import dbService from '@/service/db.service';
import userStore from '@/store/user.store';

const SYNC_QUEUE_KEY = 'triad_offline_sync_queue';

class SyncService {
    constructor() {
        this.isProcessing = false;
        this.inMemoryQueue = null;
        if (typeof window !== 'undefined') {
            this.init();
            window.addEventListener('online', () => {
                this.processQueue();
            });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible' && navigator.onLine) {
                    this.processQueue();
                }
            });
            if (navigator.serviceWorker) {
                navigator.serviceWorker.addEventListener('message', (event) => {
                    if (event.data && event.data.type === 'PROCESS_SYNC_QUEUE') {
                        this.processQueue();
                    }
                });
            }
            // Initial check on load
            setTimeout(() => {
                if (navigator.onLine) {
                    this.processQueue();
                }
            }, 500);
        }
    }

    async init() {
        try {
            const stored = await dbService.getItem(SYNC_QUEUE_KEY);
            if (Array.isArray(stored)) {
                this.inMemoryQueue = stored;
            } else {
                this.inMemoryQueue = this.getQueueFromLocal();
            }
            appEvents.emit('sync:queue-changed', { count: this.getPendingCount() });
        } catch (e) {
            this.inMemoryQueue = this.getQueueFromLocal();
        }
    }

    getQueueFromLocal() {
        try {
            const raw = localStorage.getItem(SYNC_QUEUE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch (e) {
            return [];
        }
    }

    getQueue() {
        if (this.inMemoryQueue !== null) {
            return this.inMemoryQueue;
        }
        return this.getQueueFromLocal();
    }

    hasPending(urlPattern) {
        const queue = this.getQueue();
        if (!urlPattern) return queue.length > 0;
        return queue.some(item => item.url && item.url.includes(urlPattern));
    }

    saveQueue(queue) {
        this.inMemoryQueue = queue;
        try {
            localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
        } catch (e) {
            console.warn('[SyncService] LocalStorage quota limit reached, saving in IndexedDB:', e);
        }
        dbService.setItem(SYNC_QUEUE_KEY, queue).catch(err => {
            console.warn('[SyncService] Error saving queue to IndexedDB:', err);
        });
        appEvents.emit('sync:queue-changed', { count: queue.length });
    }

    enqueue(item) {
        const queue = [...this.getQueue()];
        const currentUserId = userStore?.state?.userInfo?.id || 'anon';
        
        // Coalesce / deduplicate by unique action key if same entity is modified repeatedly
        const itemKey = `${item.method}:${item.url}`;
        const existingIdx = queue.findIndex(q => `${q.method}:${q.url}` === itemKey);
        
        const queueItem = {
            id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Date.now().toString(),
            userId: item.userId || currentUserId,
            url: item.url,
            method: item.method || 'POST',
            body: item.body || null,
            headers: item.headers || { 'Content-Type': 'application/json' },
            type: item.type || this.detectType(item.url),
            timestamp: Date.now()
        };

        if (existingIdx !== -1) {
            queue[existingIdx] = queueItem;
        } else {
            queue.push(queueItem);
        }

        this.saveQueue(queue);
        this.requestBackgroundSync();
        console.log(`[SyncService] Enqueued offline action: ${itemKey} for user ${currentUserId} (Total: ${queue.length})`);
        return queueItem;
    }

    detectType(url) {
        if (!url) return 'generic';
        if (url.includes('/notes')) return 'notes';
        if (url.includes('/drawing')) return 'drawing';
        if (url.includes('/notation')) return 'notation';
        if (url.includes('/favorite')) return 'favorite';
        if (url.includes('/transpose')) return 'transpose';
        return 'generic';
    }

    async requestBackgroundSync() {
        try {
            if ('serviceWorker' in navigator && 'SyncManager' in window) {
                const registration = await navigator.serviceWorker.ready;
                await registration.sync.register('triad-sync-queue');
            }
        } catch (e) {
            // Background sync is not supported or failed to register; normal online listener will handle it
        }
    }

    // Drena a fila compartilhando a promise da execução em curso: chamadores
    // concorrentes (window online + recovery do interceptor) aguardam o MESMO
    // replay em vez de disparar reload antes do replay terminar (corrida que
    // deixava o cliente com a versão antiga após o reconnect).
    drain() {
        if (!this._drainPromise) {
            this._drainPromise = Promise.resolve(this.processQueue()).finally(() => {
                this._drainPromise = null;
            });
        }
        return this._drainPromise;
    }

    async processQueue() {
        if (this.isProcessing || !navigator.onLine) return 0;

        const queue = [...this.getQueue()];
        if (queue.length === 0) return 0;

        this.isProcessing = true;
        try {
        const currentUserId = userStore?.state?.userInfo?.id || null;
        console.log(`[SyncService] Starting sync for ${queue.length} pending operations (active user: ${currentUserId || 'unknown'})...`);

        const remainingQueue = [];
        let successCount = 0;

        for (const item of queue) {
            // If this action belongs to a specific user and a different user is currently logged in, defer it!
            if (currentUserId && item.userId && item.userId !== 'anon' && item.userId !== currentUserId) {
                console.log(`[SyncService] Deferring action for user ${item.userId} (logged in: ${currentUserId})`);
                remainingQueue.push(item);
                continue;
            }

            try {
                let parsedHeaders = item.headers || {};
                if (!(parsedHeaders instanceof Headers) && typeof parsedHeaders === 'object') {
                    parsedHeaders = { ...parsedHeaders, 'Content-Type': 'application/json' };
                }

                const response = await fetch(item.url, {
                    method: item.method,
                    headers: parsedHeaders,
                    body: typeof item.body === 'string' ? item.body : (item.body ? JSON.stringify(item.body) : undefined)
                });

                if (response.ok) {
                    successCount++;
                    console.log(`[SyncService] Successfully synced: ${item.method} ${item.url}`);
                } else if (response.status >= 400 && response.status < 500) {
                    // Client error (e.g. 404/403) - do not retry indefinitely
                    console.warn(`[SyncService] Dropping failed item ${item.url} (${response.status})`);
                } else {
                    // Server error (5xx) or temporary network issue - keep in queue
                    remainingQueue.push(item);
                }
            } catch (err) {
                console.warn(`[SyncService] Failed to sync item ${item.url}, will retry later:`, err);
                remainingQueue.push(item);
            }
        }

        try {
            this.saveQueue(remainingQueue);
        } finally {
            // Flag sempre resetada: sem isso, uma exceção aqui travava todos os drains
            // futuros e o reload pós-reconnect lia o backend pré-replay (versão antiga).
            this.isProcessing = false;
        }

        if (successCount > 0) {
            try {
                const msg = i18n.global.t('toast.offlineSyncSuccess', { count: successCount }) || 
                           `Sincronização concluída: ${successCount} alteração(ões) enviada(s) com sucesso.`;
                toastStore.dispatch('addMessage', {
                    message: msg,
                    icon: 'success',
                    ttl: 4000
                });
            } catch (e) {}

            appEvents.emit('sync:completed', { count: successCount });

            // Invalidate Service Worker cache to reflect synchronized changes
            if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'INVALIDATE_API_CACHE'
                });
            }
        }
        return successCount;
        } catch (e) {
            console.warn('[SyncService] processQueue failed:', e);
            this.isProcessing = false;
            return 0;
        }
    }

    getPendingCount() {
        return this.getQueue().length;
    }

    getQueueDetails() {
        const tq = (key, fallback) => {
            try {
                return i18n.global.t(key) || fallback;
            } catch (e) {
                return fallback;
            }
        };
        return this.getQueue().map(item => {
            let label = tq('syncQueue.pendingChange', 'Alteração pendente');
            let icon = 'document';
            if (item.url.includes('/notes')) {
                label = tq('syncQueue.notes', 'Anotações');
                icon = 'note';
            } else if (item.url.includes('/drawing')) {
                label = tq('syncQueue.drawing', 'Desenhos e rabiscos');
                icon = 'pencil';
            } else if (item.url.includes('/favorite')) {
                label = tq('syncQueue.favorite', 'Favorito');
                icon = 'star';
            } else if (item.url.includes('/notation') || item.url.includes('/transpose')) {
                label = tq('syncQueue.transpose', 'Tom / Transposição');
                icon = 'music';
            }
            return {
                id: item.id,
                url: item.url,
                method: item.method,
                label,
                icon,
                timestamp: item.timestamp
            };
        });
    }

    clearQueue() {
        this.saveQueue([]);
    }
}

export const syncService = new SyncService();
export default syncService;

